import {
  Avatar,
  Box,
  Flex,
  Heading,
  Modal,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { FC, useMemo } from "react";
import VerifiedDot from "../../atoms/VerifiedDot";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Ico } from "@/assets/icons";
import { Platform, PlatformObject, User } from "@/schemaTypes";
import { IconButton } from "@/shared/components/atoms/IconButton/IconButton";
import { colors } from "@/components/Theme/colors";

interface ChannelLeaderModalProps {
  leader?: Partial<User> | null;
  isOpen: boolean;
  onClose: () => void;
}

const socialPlatformIcon: Record<string, React.ElementType> = {
  [Platform.Facebook]: Ico.Facebook,
  [Platform.Instagram]: Ico.Instagram,
  [Platform.Linkedin]: Ico.LinkedIn,
  [Platform.Youtube]: Ico.YouTube,
};

const ChannelLeaderModal: FC<ChannelLeaderModalProps> = ({
  leader,
  isOpen,
  onClose,
}) => {
  const joinDate = useMemo(() => {
    if (!leader?.createdAt) return null;
    try {
      return format(new Date(leader.createdAt), "d 'de' MMMM 'de' yyyy", {
        locale: es,
      });
    } catch {
      return null;
    }
  }, [leader?.createdAt]);

  const orderedSocialLinks = useMemo(() => {
    if (!leader?.socialLinks) return [];
    const order = [
      Platform.Instagram,
      Platform.Linkedin,
      Platform.Youtube,
      Platform.Facebook,
    ];
    return (leader.socialLinks as (PlatformObject | null | undefined)[])
      .filter((s): s is PlatformObject => !!s?.platform && !!s?.url)
      .sort(
        (a, b) =>
          order.indexOf(a.platform as Platform) -
          order.indexOf(b.platform as Platform)
      );
  }, [leader?.socialLinks]);

  const leaderWebsites = useMemo(() => {
    return leader?.website?.filter((w): w is string => !!w) ?? [];
  }, [leader?.website]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent
        borderRadius="6px"
        px={{ base: "16px", md: "32px" }}
        mx={{ base: "16px", md: "0px" }}
        pt="24px"
        pb="32px"
      >
        <IconButton
          onClick={onClose}
          variant="link"
          ariaLabel="Cerrar"
          icon={<Ico.Close />}
          h={"24px"}
          w={"24px"}
          size="sm"
          color="black"
          ml="auto"
        />

        <Flex
          align={{ base: "flex-start", md: "center" }}
          gap={3}
          flexDir={{ base: "column", md: "row" }}
        >
          {leader && (
            <Avatar
              src={leader.avatarUrl ?? undefined}
              name={`${leader.name} ${leader.lastName ?? ""}`}
              height="112px"
              width="112px"
            />
          )}
          <Box>
            <Flex align="center" gap={2} mb={1}>
              <Heading as="h5">
                {leader ? `${leader.name} ${leader.lastName ?? ""}` : ""}
              </Heading>
              <VerifiedDot size={20} />
            </Flex>
            {leader?.headline && (
              <Text variant="caption">{leader.headline}</Text>
            )}
          </Box>
        </Flex>
        {leader && (
          <VStack align="stretch" spacing={6} mt="16px">
            {leader.mentorDescription && (
              <Text variant="placeholder" whiteSpace="pre-line">
                {leader.mentorDescription}
              </Text>
            )}

            {orderedSocialLinks.length > 0 && (
              <Box>
                <VStack align="stretch" spacing={3} fontSize="sm">
                  {orderedSocialLinks.map((s, i) => {
                    const Icon = socialPlatformIcon[s.platform as Platform];
                    const display = s.url
                      ?.replace(/^https?:\/\//, "")
                      .replace(/www\./, "");
                    return (
                      <Flex
                        key={`${s.platform}-${i}`}
                        gap={3}
                        align="flex-start"
                        alignItems={"center"}
                      >
                        <Box>{Icon && <Icon fontSize="20px" />}</Box>
                        <ChakraLink
                          href={s.url ?? undefined}
                          target="_blank"
                          rel="noopener noreferrer"
                          wordBreak="break-all"
                          color={colors.primary[300]}
                          textDecoration="none"
                        >
                          <Text variant="placeholder">{display}</Text>
                        </ChakraLink>
                      </Flex>
                    );
                  })}
                </VStack>
              </Box>
            )}

            <Box>
              <VStack align="stretch" spacing={3} fontSize="sm">
                {leaderWebsites.map((website, i) => (
                  <Flex align="center" gap={2} key={i}>
                    <Ico.Globe fontSize="18px" />
                    <ChakraLink
                      href={website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Text variant="placeholder">
                        {website
                          .replace(/^https?:\/\//, "")
                          .replace(/www\./, "")}
                      </Text>
                    </ChakraLink>
                  </Flex>
                ))}
                {leader?.countryData?.name && (
                  <Flex align="center" gap={2}>
                    <Ico.LocationPin fontSize="18px" />
                    <Text variant="placeholder">{leader.countryData.name}</Text>
                  </Flex>
                )}
                {joinDate && (
                  <Flex align="center" gap={2}>
                    <Ico.MessageInfo fontSize="18px" />
                    <Text variant="placeholder">{`Se unió el ${joinDate}`}</Text>
                  </Flex>
                )}
              </VStack>
            </Box>
          </VStack>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ChannelLeaderModal;
